import React, { useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css"; // Import AOS styles
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../../context/ThemeContext";

const GalleryCertificates = () => {
    const data = [
        {
            label: "Technical",
            value: "technical",
            images: [
                {
                    imageLink: `${process.env.PUBLIC_URL}/media/certificates/architecture.jpg`,
                    name: "CS301 - Computer Architecture",
                },
                {
                    imageLink: `${process.env.PUBLIC_URL}/media/certificates/evolveAi2023.png`,
                    name: "Ai Hackathon - NHCE",
                },
                {
                    imageLink: `${process.env.PUBLIC_URL}/media/certificates/docker.jpg`,
                    name: "Docker Essentials - IBM",
                },
                {
                    imageLink: `${process.env.PUBLIC_URL}/media/certificates/quantum2023.jpg`,
                    name: "Design Thinking Workshop - NHCE",
                },
                {
                    imageLink: `${process.env.PUBLIC_URL}/media/certificates/ace.jpg`,
                    name: "Cyber Security Competition - Ace Academy",
                },
                {
                    imageLink: `${process.env.PUBLIC_URL}/media/certificates/chatbot.jpg`,
                    name: "Building ChatBot - IBM",
                },
                {
                    imageLink: `${process.env.PUBLIC_URL}/media/certificates/os.jpg`,
                    name: "CS401 - Operating System",
                },
                {
                    imageLink: `${process.env.PUBLIC_URL}/media/certificates/cisco-hacker.jpg`,
                    name: "Cisco networking academy ethical hacker certification",
                },
            ],
        },
        {
            label: "NonTechnical",
            value: "nontechnical",
            images: [
                {
                    imageLink: `${process.env.PUBLIC_URL}/media/certificates/lionsBlood01.jpg`,
                    name: "Blood Donation - NHCE",
                },
            ],
        },
    ];

    const [activeTab, setActiveTab] = useState(data[0].value);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCert, setSelectedCert] = useState(null);

    const openModal = (cert) => {
        setSelectedCert(cert);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedCert(null);
    };

    useEffect(() => {
        AOS.init({
            duration: 800,
            once: false,
        });
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                closeModal();
            }
        };

        if (isModalOpen) {
            window.addEventListener("keydown", handleKeyDown);
        } else {
            window.removeEventListener("keydown", handleKeyDown);
        }

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isModalOpen]);

    const overlayVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
    };

    const modalVariants = {
        hidden: { scale: 0.8, opacity: 0 },
        visible: { scale: 1, opacity: 1 },
        exit: { scale: 0.8, opacity: 0 },
    };

    const { theme } = useTheme();

    return (
        <>
            <div className="ubuntu-regular">
                {/* Small screen (only zoom-in effect) */}
                <div className="container mx-auto rounded-lg shadow p-4 lg:hidden md:hidden">
                    <h2 className="text-3xl font-bold text-left p-4">
                        Certificates{" "}
                        <span className="text-sm font-normal text-gray-500">
                            (more to be added)
                        </span>
                    </h2>
                    <div
                        className="flex justify-center mb-4 space-x-4"
                        data-aos="zoom-in"
                    >
                        {data.map(({ label, value }) => (
                            <button
                                key={value}
                                onClick={() => setActiveTab(value)}
                                className={`py-2 px-4 rounded transition-colors duration-300 transform hover:scale-110 ${
                                    activeTab === value
                                        ? "bg-yellow-500"
                                        : "bg-gray-100 text-black"
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        {data
                            .find((tab) => tab.value === activeTab)
                            ?.images.map(({ imageLink, name }, index) => (
                                <div
                                    key={index}
                                    className="cursor-pointer"
                                    onClick={() =>
                                        openModal({ src: imageLink, name })
                                    }
                                    data-aos="zoom-in"
                                >
                                    <img
                                        className="h-40 w-full max-w-full rounded-lg object-cover object-center hover:opacity-80 transition-opacity duration-300"
                                        src={imageLink}
                                        alt={`certificate-${index}`}
                                    />
                                </div>
                            ))}
                    </div>
                </div>

                {/* Large screen (keep current settings) */}
                <div className="container mx-auto rounded-lg shadow p-4 hidden lg:block md:block">
                    <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-left">
                        Certificates{" "}
                        <span className="text-sm font-normal text-gray-500">
                            (more to be added)
                        </span>
                    </h2>
                    <div
                        className="flex justify-center mb-4 space-x-4"
                        data-aos="zoom-out-down"
                    >
                        {data.map(({ label, value }) => (
                            <button
                                key={value}
                                onClick={() => setActiveTab(value)}
                                className={`py-2 px-4 rounded transition-colors duration-300 transform hover:scale-110 ${
                                    activeTab === value
                                        ? "bg-yellow-500"
                                        : "bg-gray-100 text-black "
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                        {data
                            .find((tab) => tab.value === activeTab)
                            ?.images.map(({ imageLink, name }, index) => (
                                <div
                                    key={index}
                                    className="cursor-pointer"
                                    onClick={() =>
                                        openModal({ src: imageLink, name })
                                    }
                                    data-aos="flip-left" // Keep flip-left for large screens
                                >
                                    <img
                                        className="h-40 w-full max-w-full rounded-lg object-cover object-center hover:opacity-80 transition-opacity duration-300"
                                        src={imageLink}
                                        alt={`certificate-${index}`}
                                    />
                                </div>
                            ))}
                    </div>
                </div>

                {/* Modal with Framer Motion Animations */}
                <AnimatePresence>
                    {isModalOpen && selectedCert && (
                        <motion.div
                            className={`fixed inset-0 z-50 flex items-center justify-center px-4 ${
                                theme === "dark"
                                    ? "bg-gray-900 bg-opacity-90 text-white"
                                    : "bg-gray-900 bg-opacity-80 text-white"
                            }`}
                            onClick={closeModal}
                            aria-modal="true"
                            role="dialog"
                            variants={overlayVariants}
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                            transition={{ duration: 0.3 }}
                        >
                            <motion.div
                                className="rounded-lg p-4 sm:p-6 lg:p-8 relative max-w-full sm:max-w-3xl lg:max-w-5xl mx-auto"
                                onClick={(e) => e.stopPropagation()}
                                variants={modalVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                                transition={{ duration: 0.3 }}
                            >
                                <img
                                    src={selectedCert.src}
                                    alt={selectedCert.name}
                                    className="w-full h-auto rounded"
                                />
                                <p className="mt-4 text-center text-sm sm:text-base">
                                    {selectedCert.name}
                                </p>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </>
    );
};

export default GalleryCertificates;
